/**
 * Full Belly Wine Imports — Theme JavaScript
 * Vanilla JS only. No frameworks. No jQuery.
 * Loaded with defer — runs after DOM is ready.
 *
 * Modules:
 *  1. Sticky header + hero transparency
 *  2. Mega-nav (desktop hover + keyboard)
 *  3. Mobile menu (overlay, focus trap, accordion)
 *  4. Scroll reveal (IntersectionObserver)
 *  5. Product image gallery (thumbnail swap)
 *  6. Announcement bar dismiss (sessionStorage)
 *  7. FAQ accordion
 *  8. Predictive search modal
 */

(function () {
  'use strict';

  /* ============================================================
     Utilities
  ============================================================ */

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
          'select:not([disabled]), textarea:not([disabled]), ' +
          '[tabindex]:not([tabindex="-1"])'
      )
    );
  }

  function trapFocus(container, onEscape) {
    var focusable = getFocusableElements(container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    function handler(e) {
      if (e.key === 'Escape' && typeof onEscape === 'function') {
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', handler);
    first.focus();

    return function removeTrap() {
      container.removeEventListener('keydown', handler);
    };
  }

  function formatMoney(cents) {
    var amount = (parseInt(cents, 10) / 100).toFixed(2);
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    return fmt.replace('{{amount}}', amount)
              .replace('{{amount_no_decimals}}', Math.round(parseFloat(amount)));
  }

  /* ============================================================
     Module 1 — Sticky header + hero transparency
  ============================================================ */

  function initStickyHeader() {
    var header = document.getElementById('site-header');
    if (!header) return;
    var hero = document.querySelector('.hero');

    function calcStickyAt() {
      if (!hero) return 50;
      // getBoundingClientRect().top + scrollY = document-absolute position,
      // immune to iOS chrome collapse that shifts viewport-relative measurements
      var heroTop = hero.getBoundingClientRect().top + window.scrollY;
      var heroHeight = hero.offsetHeight;
      return window.innerWidth < 1024
        ? heroTop + heroHeight * 0.5
        : heroTop + heroHeight;
    }

    var stickyAt = calcStickyAt();

    var leaveTimer;

    function setSticky(shouldBeSticky) {
      if (shouldBeSticky) {
        clearTimeout(leaveTimer);
        document.body.classList.remove('is-sticky-leaving');
        document.body.classList.add('is-sticky-prep');
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            document.body.classList.remove('is-sticky-prep');
            document.body.classList.add('is-sticky');
          });
        });
      } else if (document.body.classList.contains('is-sticky')) {
        clearTimeout(leaveTimer);
        document.body.classList.remove('is-sticky');
        document.body.classList.add('is-sticky-leaving');
        leaveTimer = setTimeout(function () {
          document.body.classList.remove('is-sticky-leaving');
        }, 320);
      }
    }

    function updateHeader() {
      if (!hero) {
        setSticky(window.scrollY > 50);
        return;
      }
      setSticky(window.scrollY >= stickyAt);
    }

    window.addEventListener('resize', function () {
      stickyAt = calcStickyAt();
      updateHeader();
    });

    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();
  }

  /* ============================================================
     Module 2 — Mega-nav (desktop)
  ============================================================ */

  function initMegaNav() {
    var shopTrigger = document.getElementById('shop-trigger');
    var megaNav = document.getElementById('mega-nav');
    var navShop = document.getElementById('nav-shop');
    var header = document.getElementById('site-header');
    if (!shopTrigger || !megaNav || !navShop) return;

    var closeTimer;

    function openMega() {
      clearTimeout(closeTimer);
      if (header) megaNav.style.top = header.getBoundingClientRect().bottom + 'px';
      megaNav.classList.add('is-open');
      navShop.classList.add('is-open');
      shopTrigger.setAttribute('aria-expanded', 'true');
    }

    function closeMega() {
      closeTimer = setTimeout(function () {
        megaNav.classList.remove('is-open');
        navShop.classList.remove('is-open');
        shopTrigger.setAttribute('aria-expanded', 'false');
      }, 180);
    }

    navShop.addEventListener('mouseenter', openMega);
    navShop.addEventListener('mouseleave', closeMega);
    megaNav.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    megaNav.addEventListener('mouseleave', closeMega);

    shopTrigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (megaNav.classList.contains('is-open')) {
        closeMega();
      } else {
        openMega();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMega();
    });

    document.addEventListener('click', function (e) {
      if (!navShop.contains(e.target) && !megaNav.contains(e.target)) {
        megaNav.classList.remove('is-open');
        navShop.classList.remove('is-open');
        shopTrigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ============================================================
     Module 3 — Mobile menu
  ============================================================ */

  function initMobileMenu() {
    var openBtn = document.getElementById('mobile-menu-btn');
    var menu = document.getElementById('mobile-menu');
    var closeBtn = menu ? menu.querySelector('.mobile-menu__close') : null;
    var backdrop = document.getElementById('mobile-menu-backdrop');
    if (!openBtn || !menu) return;

    var removeTrap = null;
    var previousFocus = null;

    function openMenu() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      openBtn.setAttribute('aria-label', 'Close navigation menu');
      document.body.style.overflow = 'hidden';
      if (backdrop) backdrop.classList.add('is-visible');
      previousFocus = document.activeElement;
      removeTrap = trapFocus(menu, closeMenu);
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.setAttribute('aria-label', 'Open navigation menu');
      document.body.style.overflow = '';
      if (backdrop) backdrop.classList.remove('is-visible');
      if (typeof removeTrap === 'function') {
        removeTrap();
        removeTrap = null;
      }
      if (previousFocus) previousFocus.focus();
    }

    openBtn.addEventListener('click', function () {
      if (menu.classList.contains('is-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', closeMenu);
    }

    if (backdrop) {
      backdrop.addEventListener('click', closeMenu);
    }

    // Accordion for sub-menus (Shop > columns)
    var accordionTriggers = menu.querySelectorAll('[data-mobile-accordion]');
    accordionTriggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var subList = btn.nextElementSibling;
        if (!subList) return;

        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));

        if (expanded) {
          subList.setAttribute('hidden', '');
        } else {
          subList.removeAttribute('hidden');
        }

        // Rotate chevron via aria-expanded CSS selector in theme.css
      });
    });
  }

  /* ============================================================
     Module 4 — Scroll reveal
  ============================================================ */

  function initScrollReveal() {
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      // Skip animation — make all reveal elements visible immediately
      document.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ============================================================
     Module 5 — Product image gallery
  ============================================================ */

  function initProductGallery() {
    var mainImg = document.querySelector('.product-gallery__main img');
    var thumbs = document.querySelectorAll('.product-gallery__thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var newSrc = thumb.getAttribute('data-image-src') || thumb.querySelector('img').src;
        var newAlt = thumb.getAttribute('data-image-alt') || mainImg.alt;
        var newZoomSrc = thumb.getAttribute('data-zoom-src');

        mainImg.src = newSrc;
        mainImg.alt = newAlt;
        if (newZoomSrc) mainImg.setAttribute('data-zoom-src', newZoomSrc);

        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });

      // Keyboard activation
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          thumb.click();
        }
      });
    });
  }

  /* ============================================================
     Module 6 — Announcement bar dismiss
  ============================================================ */

  function initAnnouncementBar() {
    var bar = document.getElementById('announcement-bar');
    if (!bar) return;

    // Hide if previously dismissed this session
    if (sessionStorage.getItem('announcementDismissed') === 'true') {
      bar.style.display = 'none';
      return;
    }

    var closeBtn = document.getElementById('announcement-bar-close');
    if (!closeBtn) return;

    closeBtn.addEventListener('click', function () {
      bar.style.display = 'none';
      sessionStorage.setItem('announcementDismissed', 'true');
    });
  }

  /* ============================================================
     7. FAQ accordion
  ============================================================ */

  function initFaqAccordion() {
    var questions = document.querySelectorAll('.faq-question');
    if (!questions.length) return;

    questions.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        var answer = btn.nextElementSibling;
        btn.setAttribute('aria-expanded', String(!expanded));
        if (expanded) {
          answer.setAttribute('hidden', '');
        } else {
          answer.removeAttribute('hidden');
        }
      });
    });
  }

  /* ============================================================
     Module 8 — Predictive search modal
  ============================================================ */

  function initPredictiveSearch() {
    var openBtn  = document.getElementById('search-modal-btn');
    var modal    = document.getElementById('search-modal');
    var backdrop = document.getElementById('search-modal-backdrop');
    var input    = document.getElementById('search-modal-input');
    var results  = document.getElementById('search-modal-results');
    var clearBtn = document.getElementById('search-modal-clear');
    var footer   = document.getElementById('search-modal-footer');
    var allLink  = document.getElementById('search-modal-all-link');
    var closeBtn = modal ? modal.querySelector('.search-modal__close') : null;

    if (!openBtn || !modal || !input) return;

    var removeTrap    = null;
    var previousFocus = null;
    var debounceTimer = null;
    var currentQuery  = '';

    function openModal() {
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-visible');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      previousFocus = document.activeElement;
      removeTrap = trapFocus(modal, closeModal);
      setTimeout(function () { input.focus(); }, 350);
    }

    function closeModal() {
      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-visible');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (typeof removeTrap === 'function') { removeTrap(); removeTrap = null; }
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
      input.value = '';
      currentQuery = '';
      clearResults();
    }

    function clearResults() {
      results.innerHTML = '';
      if (footer) footer.hidden = true;
      if (clearBtn) clearBtn.hidden = true;
    }

    function onInput() {
      var q = input.value.trim();
      if (clearBtn) clearBtn.hidden = (q.length === 0);
      if (q.length === 0) { currentQuery = ''; clearResults(); return; }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { fetchSuggestions(q); }, 280);
    }

    function fetchSuggestions(q) {
      if (q === currentQuery) return;
      currentQuery = q;
      var url =
        '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,vendor,body,tag' +
        '&resources[options][unavailable_products]=last';
      fetch(url)
        .then(function (res) { if (!res.ok) throw new Error(); return res.json(); })
        .then(function (data) {
          if (q !== currentQuery) return;
          var products =
            (data.resources && data.resources.results && data.resources.results.products) || [];
          renderResults(products, q);
        })
        .catch(function () { clearResults(); });
    }

    function renderResults(products, q) {
      if (!products.length) {
        results.innerHTML =
          '<p class="search-modal__no-results">No products found for <em>' +
          escapeHtml(q) + '</em></p>';
        if (footer) footer.hidden = true;
        return;
      }

      var html = '';
      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        var imgUrl = '';
        if (p.featured_image && p.featured_image.url) {
          imgUrl = p.featured_image.url.replace(/(\.[^.?]+)(\?.*)?$/, '_160x160$1$2');
        }
        html +=
          '<a class="search-modal__result" href="' + escapeHtml(p.url) + '" role="option">' +
            '<div class="search-modal__result-image">' +
              (imgUrl
                ? '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="64" height="64">'
                : '<div class="search-modal__result-image-placeholder"></div>') +
            '</div>' +
            '<div class="search-modal__result-body">' +
              (p.vendor ? '<span class="search-modal__result-vendor">' + escapeHtml(p.vendor) + '</span>' : '') +
              '<span class="search-modal__result-title">' + escapeHtml(p.title) + '</span>' +
              (p.price !== undefined
                ? '<span class="search-modal__result-price">' + formatMoney(Math.round(parseFloat(p.price) * 100)) + '</span>'
                : '') +
            '</div>' +
          '</a>';
      }
      results.innerHTML = html;

      if (footer && allLink) {
        allLink.href = '/search?q=' + encodeURIComponent(q) + '&type=product';
        footer.hidden = false;
      }
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    openBtn.addEventListener('click', function () {
      modal.classList.contains('is-open') ? closeModal() : openModal();
    });
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);
    if (clearBtn) clearBtn.addEventListener('click', function () {
      input.value = ''; currentQuery = ''; clearResults(); input.focus();
    });
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        var q = input.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q) + '&type=product';
      }
    });
  }

  /* ============================================================
     Module 9 — Cart (AJAX quantity updates, removal, bottle deposit)
  ============================================================ */

  var depositVariantId = null;

  function calcTotalBottles(cartData) {
    return cartData.items.reduce(function (total, item) {
      if (item.handle === 'bottle-deposit') return total;
      var bpc = parseInt(
        (item.properties && item.properties['_bottles_per_case']) || 12, 10
      );
      if (!(bpc > 0)) bpc = 12;
      return total + item.quantity * bpc;
    }, 0);
  }

  function syncDeposit(cartData) {
    if (!depositVariantId) return;

    var totalBottles = calcTotalBottles(cartData);
    var depositItem = cartData.items.find(function (i) { return i.handle === 'bottle-deposit'; });
    var lastItem = cartData.items[cartData.items.length - 1];
    var isDepositLast = !!depositItem && !!lastItem && lastItem.handle === 'bottle-deposit';

    // Case 1: no bottles remain — remove deposit and reload for empty state
    if (totalBottles === 0) {
      if (!depositItem) return;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: depositItem.key, quantity: 0 })
      })
        .then(function (res) { return res.json(); })
        .then(function () { location.reload(); })
        .catch(function () {});
      return;
    }

    // Case 2: deposit exists, correct quantity, already last — nothing to do
    if (depositItem && depositItem.quantity === totalBottles && isDepositLast) return;

    // Case 3: deposit exists but wrong quantity OR not last — remove then re-add at end
    if (depositItem) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: depositItem.key, quantity: 0 })
      })
        .then(function (res) { return res.json(); })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: depositVariantId, quantity: totalBottles })
          });
        })
        .then(function (res) { return res.json(); })
        .then(function () { return fetch('/cart.js'); })
        .then(function (res) { return res.json(); })
        .then(function (updatedCart) {
          var subtotalEl = document.querySelector('[data-cart-subtotal]');
          if (subtotalEl) subtotalEl.textContent = formatMoney(updatedCart.items_subtotal_price);
        })
        .catch(function () {});
      return;
    }

    // Case 4: deposit does not exist yet — add it (appends to end naturally); reload to refresh subtotal
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: depositVariantId, quantity: totalBottles })
    })
      .then(function () { location.reload(); })
      .catch(function () {});
  }

  function initCart() {
    var cartEl = document.getElementById('main-cart');
    if (!cartEl) return;

    fetch('/products/bottle-deposit.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (p) {
        if (p && p.variants && p.variants[0]) depositVariantId = p.variants[0].id;
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cartData) { syncDeposit(cartData); })
      .catch(function () {});

    cartEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-increase], [data-qty-decrease], [data-cart-remove]');
      if (!btn) return;
      var item = btn.closest('[data-cart-item]');
      var key = item.dataset.key;
      var qtyInput = item.querySelector('[data-qty-input]');
      var currentQty = parseInt(qtyInput.value, 10);
      var newQty;
      if (btn.hasAttribute('data-cart-remove')) {
        newQty = 0;
      } else if (btn.hasAttribute('data-qty-increase')) {
        newQty = currentQty + 1;
      } else {
        newQty = Math.max(0, currentQty - 1);
      }
      updateCartItem(key, newQty, item, qtyInput);
    });

    cartEl.addEventListener('change', function (e) {
      if (!e.target.matches('[data-qty-input]')) return;
      var item = e.target.closest('[data-cart-item]');
      var key = item.dataset.key;
      var newQty = Math.max(0, parseInt(e.target.value, 10) || 0);
      updateCartItem(key, newQty, item, e.target);
    });
  }

  function updateCartItem(key, qty, itemEl, qtyInput) {
    itemEl.classList.add('cart__item--is-loading');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (res) { if (!res.ok) throw new Error(); return res.json(); })
      .then(function (cart) {
        if (qty === 0) {
          itemEl.remove();
        } else {
          qtyInput.value = qty;
          itemEl.classList.remove('cart__item--is-loading');
        }
        var subtotalEl = document.querySelector('[data-cart-subtotal]');
        if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
        var wineCount = cart.items.filter(function (i) {
          return i.handle !== 'bottle-deposit';
        }).length;
        if (wineCount === 0) {
          location.reload();
        } else {
          syncDeposit(cart);
        }
      })
      .catch(function () {
        itemEl.classList.remove('cart__item--is-loading');
      });
  }

  /* ============================================================
     Module 10 — Product image lightbox
  ============================================================ */

  function initProductLightbox() {
    var mainImg = document.getElementById('product-main-image');
    if (!mainImg) return;

    var backdrop = document.createElement('div');
    backdrop.id = 'product-lightbox-backdrop';

    var lightbox = document.createElement('div');
    lightbox.id = 'product-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Product image zoom');

    var lbImg = document.createElement('img');
    lbImg.id = 'product-lightbox-img';
    lightbox.appendChild(lbImg);

    document.body.appendChild(backdrop);
    document.body.appendChild(lightbox);

    function openLightbox() {
      var zoomSrc = mainImg.getAttribute('data-zoom-src') || mainImg.src;
      lbImg.src = zoomSrc;
      lbImg.alt = mainImg.alt;
      lightbox.scrollTop = 0;
      backdrop.classList.add('is-open');
      lightbox.classList.add('is-open');
      document.body.classList.add('lightbox-open');
    }

    function closeLightbox() {
      backdrop.classList.remove('is-open');
      lightbox.classList.remove('is-open');
      document.body.classList.remove('lightbox-open');
    }

    mainImg.addEventListener('click', openLightbox);
    lightbox.addEventListener('click', closeLightbox);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLightbox();
    });
  }

  /* ============================================================
     Init all modules
  ============================================================ */

  document.addEventListener('DOMContentLoaded', function () {
    initStickyHeader();
    initMegaNav();
    initMobileMenu();
    initScrollReveal();
    initProductGallery();
    initAnnouncementBar();
    initFaqAccordion();
    initPredictiveSearch();
    initCart();
    initProductLightbox();
  });
})();
